import { ToggleButton, ToggleButtonGroup } from '@mui/material'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { defaultOptionChainColumns, selectOptionChainColumns } from '../../../selectors/home'
import commonActions from '../../../actions/common.actions'
import {
    getAllOptionChainColumnIds,
    getCanonicalOptionChainColumnOrder,
    mergeIntoColumnOrder,
} from '../../../util/optionChainColumns'
import { useDispatch } from 'react-redux'
import { NonInteractiveTooltip } from '../../custom'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { OptionChainViewKey } from '../../../assets/constants'
import ROUTES from '../../../assets/routes'
import { matchPath } from 'react-router-dom'

const OptionChainQuickViewMenu = ({ orderOptionChain, optionChainViewKey: optionChainViewKeyProp }: {orderOptionChain: boolean, optionChainViewKey?: OptionChainViewKey}) => {
    const optionChainViewKey = useMemo(() => {
        if (optionChainViewKeyProp) return optionChainViewKeyProp
        if(matchPath(ROUTES.simulator, window.location.pathname)){
            return OptionChainViewKey.HISTORICAL
        }
        return OptionChainViewKey.LIVE
    }, [optionChainViewKeyProp, window.location.pathname])
    const chainType = orderOptionChain || optionChainViewKey === OptionChainViewKey.WIDGET ? 'extended' : 'normal'
    const selectedColumns: string[] = useSelector(selectOptionChainColumns(optionChainViewKey, chainType))
    const greekColumns = useMemo(() => (['delta', 'gamma', 'vega', 'theta', ...(orderOptionChain ? [] : ['rho'])]) , [orderOptionChain])
    const ltpColumns = useMemo(() => (orderOptionChain ? ['ltp', 'ltpChange', 'impliedVolatility', 'delta', 'openInterest'] : ['ltp', 'ltpChange', 'openInterest', 'oiChange|outlook']) , [orderOptionChain])
    const oiColums = useMemo(() => (['openInterest', 'oiChange|outlook']) , [])
    const stringifiedGreekColumns = useMemo(() => JSON.stringify(greekColumns), [greekColumns])
    const stringifiedLtpColumns = useMemo(() => JSON.stringify(ltpColumns), [ltpColumns])
    const stringifiedSelectedColumns = useMemo(() => JSON.stringify(selectedColumns), [selectedColumns])
    const [lastCustomColumns, setLastCustomColumns] = useState<string[]>([])
    const dispatch = useDispatch()
    const saveVisiblePreset = useCallback((visible: string[]) => {
        const canonical = getCanonicalOptionChainColumnOrder(chainType)
        const all = getAllOptionChainColumnIds(chainType)
        const order = mergeIntoColumnOrder(visible, all, canonical)
        const hidden = order.filter((c) => !visible.includes(c))
        commonActions.saveOptionSelectedColumns(dispatch, {
            columns: order,
            hidden,
            optionChainViewKey,
            type: chainType,
        })
    }, [dispatch, optionChainViewKey, chainType])

    const handleAllClicked = useCallback(() => {
        AnalyticsManager.logEvent("All Clicked", {source: 'option-chain-bar'})
        commonActions.saveOptionSelectedColumns(dispatch, {
            columns: defaultOptionChainColumns,
            hidden: [],
            optionChainViewKey: optionChainViewKey,
            type: chainType,
        })
    }, [dispatch, optionChainViewKey, chainType])

    const handleGreeksClick = useCallback(() => {
        AnalyticsManager.logEvent("Greeks Clicked", {source: 'option-chain-bar'})
        saveVisiblePreset(greekColumns)
    }, [greekColumns, saveVisiblePreset])

    const handleLtpClick = useCallback(() => {
        AnalyticsManager.logEvent("LTP Clicked", {source: 'option-chain-bar'})
        saveVisiblePreset(ltpColumns)
    }, [ltpColumns, saveVisiblePreset])

    const handleOiClick = useCallback(() => {
        AnalyticsManager.logEvent("OI Clicked", {source: 'option-chain-bar'})
        saveVisiblePreset(oiColums)
    }, [oiColums, saveVisiblePreset])

    const handleCustomClick = useCallback(() => {
        AnalyticsManager.logEvent("Custom Clicked", {source: 'option-chain-bar'})
        if(
            stringifiedGreekColumns === stringifiedSelectedColumns ||
            stringifiedLtpColumns === stringifiedSelectedColumns ||
            JSON.stringify(defaultOptionChainColumns) === stringifiedSelectedColumns
        ){
            saveVisiblePreset(lastCustomColumns)
        }
    }, [lastCustomColumns, stringifiedSelectedColumns, stringifiedLtpColumns, stringifiedGreekColumns, saveVisiblePreset])

    const customSelected = useMemo(() => {
        return (
            stringifiedSelectedColumns !== stringifiedGreekColumns &&
            stringifiedSelectedColumns !== stringifiedLtpColumns &&
            stringifiedSelectedColumns !== JSON.stringify(defaultOptionChainColumns)
        )
    }, [stringifiedGreekColumns, stringifiedLtpColumns, stringifiedSelectedColumns])

    const customBtnValue = useMemo(() => ({}), [customSelected])

    useEffect(() => {
        if(
            stringifiedSelectedColumns !== stringifiedGreekColumns &&
            stringifiedSelectedColumns !== stringifiedLtpColumns &&
            stringifiedSelectedColumns !== JSON.stringify(defaultOptionChainColumns)
        ){
            setLastCustomColumns(selectedColumns)
        }
    }, [stringifiedSelectedColumns])
    
    return (
        <NonInteractiveTooltip title="Select quick views" arrow placement="top">
            <ToggleButtonGroup
                id={'option-chain-quik-view-group'}
                size="small"
                value={stringifiedSelectedColumns}
                aria-label="text alignment"
                sx={{
                    height: '32px',
                    '.MuiButtonBase-root': {
                        borderColor: (theme) => theme.palette.background.default
                    },
                    '&:not(first-child)': {
                        marginLeft: (theme) => theme.spacing(1)
                    }
                }}
            >
                {
                    orderOptionChain ?
                    [
                        <ToggleButton key="ltp" aria-label="right aligned" value={stringifiedLtpColumns} onClick={handleLtpClick}>
                            LTP
                        </ToggleButton>,
                        <ToggleButton key="oi" aria-label="left aligned" selected={customSelected} value={customBtnValue} onClick={handleOiClick} sx={{ color: (theme) => customSelected ? theme.palette.text.primary : theme.palette.text.secondary }}>
                            OI
                        </ToggleButton>,
                        <ToggleButton key="greeks" aria-label="centered" value={stringifiedGreekColumns} onClick={handleGreeksClick}>
                            Greeks
                        </ToggleButton>
                    ] :
                    [
                        ...( (lastCustomColumns?.length) ? [<ToggleButton key="custom" aria-label="left aligned" selected={customSelected} value={customBtnValue} onClick={handleCustomClick} sx={{ color: (theme) => customSelected ? theme.palette.text.primary : theme.palette.text.secondary }}>
                            Custom
                        </ToggleButton>] : []),
                        <ToggleButton key="all" aria-label="center left aligned" value={JSON.stringify(defaultOptionChainColumns)} onClick={handleAllClicked}>
                            All
                        </ToggleButton>,
                        <ToggleButton key="greeks" aria-label="center right aligned" value={stringifiedGreekColumns} onClick={handleGreeksClick}>
                            Greeks
                        </ToggleButton>,
                        <ToggleButton key="ltp" aria-label="right aligned" value={stringifiedLtpColumns} onClick={handleLtpClick}>
                            LTP
                        </ToggleButton>
                    ]
                }
            </ToggleButtonGroup>
        </NonInteractiveTooltip>
    )
}

export default memo(OptionChainQuickViewMenu)
